import { useState, useCallback } from "react";
import { AlertOctagon, Plus, CheckCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import {
  useIncidents,
  useCreateIncident,
  useResolveIncident,
} from "@/hooks/admin/useOperations";
import type {
  Incident,
  IncidentSeverity,
  IncidentStatus,
  CreateIncidentInput,
} from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  critical: "Critique",
  high: "Haute",
  medium: "Moyenne",
  low: "Basse",
};

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-stone-100 text-stone-600 border-stone-200",
};

const STATUS_LABELS: Record<IncidentStatus, string> = {
  open: "Ouvert",
  investigating: "En investigation",
  resolved: "Résolu",
  closed: "Fermé",
};

const STATUS_COLORS: Record<IncidentStatus, string> = {
  open: "bg-red-100 text-red-800 border-red-200",
  investigating: "bg-amber-100 text-amber-800 border-amber-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-stone-100 text-stone-600 border-stone-200",
};

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "severity",
    label: "Sévérité",
    type: "select",
    options: [
      { value: "critical", label: "Critique" },
      { value: "high", label: "Haute" },
      { value: "medium", label: "Moyenne" },
      { value: "low", label: "Basse" },
    ],
  },
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: [
      { value: "open", label: "Ouvert" },
      { value: "investigating", label: "En investigation" },
      { value: "resolved", label: "Résolu" },
      { value: "closed", label: "Fermé" },
    ],
  },
  { key: "date_from", label: "Du", type: "date" },
  { key: "date_to", label: "Au", type: "date" },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create incident modal
// ---------------------------------------------------------------------------

interface CreateIncidentModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateIncidentInput) => Promise<void>;
}

function CreateIncidentModal({
  open,
  onClose,
  isSaving,
  onSave,
}: CreateIncidentModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<IncidentSeverity>("medium");
  const [assignedTo, setAssignedTo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setTitle("");
    setDescription("");
    setSeverity("medium");
    setAssignedTo("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError("Le titre est requis.");
      return;
    }
    if (!description.trim()) {
      setError("La description est requise.");
      return;
    }
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        severity,
        assigned_to: assignedTo.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [title, description, severity, assignedTo, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Signaler un incident"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Signalement…" : "Signaler"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Titre de l'incident"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Décrivez l'incident en détail…"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">Sévérité</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {Object.entries(SEVERITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Assigner à (ID)
            </label>
            <input
              type="text"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              placeholder="UUID de l'utilisateur"
            />
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Incidents() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showCreate, setShowCreate] = useState(false);

  const params = {
    page,
    limit: 20,
    severity: (filters.severity as IncidentSeverity) ?? undefined,
    status: (filters.status as IncidentStatus) ?? undefined,
    date_from: (filters.date_from as string) ?? undefined,
    date_to: (filters.date_to as string) ?? undefined,
  };

  const { data, isLoading } = useIncidents(params);
  const createMutation = useCreateIncident();
  const resolveMutation = useResolveIncident();

  const incidents = data?.incidents ?? [];
  const pagination = data?.pagination;

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const openCount = incidents.filter((i) => i.status === "open").length;
  const investigatingCount = incidents.filter((i) => i.status === "investigating").length;
  const resolvedThisMonth = incidents.filter(
    (i) => i.status === "resolved" && i.resolved_at && i.resolved_at >= startOfMonth,
  ).length;
  const criticalCount = incidents.filter((i) => i.severity === "critical").length;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateIncidentInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const handleResolve = useCallback(
    async (id: string) => {
      if (!confirm("Marquer cet incident comme résolu ?")) return;
      await resolveMutation.mutateAsync(id);
    },
    [resolveMutation],
  );

  const columns: readonly ColumnDef<Incident>[] = [
    {
      id: "title",
      header: "Titre",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.title}</span>
      ),
    },
    {
      id: "severity",
      header: "Sévérité",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${SEVERITY_COLORS[row.severity]}`}
        >
          {SEVERITY_LABELS[row.severity]}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      id: "reported_by",
      header: "Signalé par",
      accessorKey: "reported_by",
    },
    {
      id: "assigned_to",
      header: "Assigné à",
      cell: (row) => (
        <span className="text-stone-500">{row.assigned_to ?? "—"}</span>
      ),
    },
    {
      id: "created_at",
      header: "Ouvert le",
      cell: (row) => (
        <span className="text-xs text-stone-400">{formatDate(row.created_at)}</span>
      ),
    },
    {
      id: "resolved_at",
      header: "Résolu le",
      cell: (row) => (
        <span className="text-xs text-stone-400">{formatDate(row.resolved_at)}</span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) =>
        row.status === "open" || row.status === "investigating" ? (
          <button
            onClick={() => handleResolve(row.id)}
            className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            title="Résoudre"
          >
            <CheckCircle className="h-3.5 w-3.5" />
            Résoudre
          </button>
        ) : null,
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <AlertOctagon className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">Incidents</h1>
              <p className="text-sm text-stone-500">Suivi et résolution des incidents</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Signaler un incident
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Ouverts" value={openCount} />
          <KPICard label="En investigation" value={investigatingCount} />
          <KPICard label="Résolus ce mois" value={resolvedThisMonth} />
          <KPICard label="Critiques" value={criticalCount} />
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar filters={FILTER_CONFIGS} values={filters} onChange={handleFilterChange} />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={incidents}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucun incident trouvé."
        />
      </div>

      <CreateIncidentModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}
